import { useForm } from "@tanstack/react-form";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  createPreviewActions,
  ShareablePreview,
} from "@/core/components/shared/preview-action";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import { NoTranslate } from "@/core/components/ui/no-translate";
import { Textarea } from "@/core/components/ui/textarea";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useUpdateSystemConfig } from "@/features/admin/api/use-update-system-config";
import type { SystemConfigDto } from "@/features/admin/api/dto";
import { useAdminTranslation } from "@/features/admin/i18n/use-admin-translation";
import {
  createSystemConfigSchema,
  type SystemConfigFormValues,
} from "@/features/admin/schemas/system-config.schema";

/**
 * Édition d'une clé de configuration système (`PUT /SystemConfigs/{key}`,
 * guide §6.8). Preview obligatoire avant submit (règle projet) ; l'auteur
 * de la modification (`updatedByKeycloakId` = claim `sub`) est tracé par
 * le hook. Rôles : flashpay-superadmin, flashpay-admin.
 */
export function SystemConfigEditDialog({
  config,
  open,
  onOpenChange,
}: {
  config: SystemConfigDto | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useAdminTranslation();
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : schéma zod traduit reconstruit sur changement de langue (règle projet).
  const schema = useMemo(() => createSystemConfigSchema(), [i18n.language]);

  const update = useUpdateSystemConfig();
  const [preview, setPreview] = useState<SystemConfigFormValues | null>(null);

  const form = useForm({
    defaultValues: {
      value: config?.value ?? "",
      description: config?.description ?? "",
    } as SystemConfigFormValues,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      setPreview(parsed.data);
    },
  });

  // Réinitialise le formulaire quand la clé éditée change.
  useEffect(() => {
    if (config) {
      form.reset();
      form.setFieldValue("value", config.value ?? "");
      form.setFieldValue("description", config.description ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset ciblé au changement de clé uniquement.
  }, [config?.key]);

  if (!config) return null;

  const handleConfirm = () => {
    if (!preview) return;
    update.mutate(
      {
        key: config.key,
        value: preview.value.trim(),
        description: preview.description?.trim() || undefined,
      },
      {
        onSuccess: () => {
          notifSilence(t("admin.configs.feedback.updated"));
          setPreview(null);
          onOpenChange(false);
        },
        onError: (error) => notifSilenceError(error.message),
      }
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>
              {t("admin.configs.actions.edit")} —{" "}
              <NoTranslate as="span" className="font-mono text-sm">
                {config.key}
              </NoTranslate>
            </DialogTitle>
          </DialogHeader>
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit();
            }}
          >
            <form.Field
              name="value"
              validators={{ onChange: fieldErrorOf(schema, "value") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="config-value">
                    {t("admin.configs.form.value")}
                  </Label>
                  <Input
                    id="config-value"
                    className="notranslate"
                    placeholder={t("admin.configs.form.valuePlaceholder")}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field name="description">
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="config-description">
                    {t("admin.configs.form.description")}
                  </Label>
                  <Textarea
                    id="config-description"
                    placeholder={t("admin.configs.form.descriptionPlaceholder")}
                    value={field.state.value ?? ""}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                </div>
              )}
            </form.Field>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={update.isPending}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={update.isPending}>
                {update.isPending ? <ButtonSpinner /> : null}
                {t("common.confirm")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {preview ? (
        <ShareablePreview
          data={{ key: config.key, ...preview }}
          title={t("admin.configs.preview.title")}
          description={t("admin.configs.preview.description")}
          mode="user"
          defaultOpen
          onOpenChange={(isOpen) => {
            if (!isOpen && !update.isPending) setPreview(null);
          }}
          maxHeight="90vh"
          actions={createPreviewActions({
            onConfirm: handleConfirm,
            onCancel: () => setPreview(null),
            t,
            confirmLoading: update.isPending,
            withIndicatorOnConfirm: true,
          })}
        />
      ) : null}
    </>
  );
}

const fieldErrorOf =
  (schema: ReturnType<typeof createSystemConfigSchema>, name: "value") =>
  (props: { value: unknown }) => {
    const shape = schema.shape[name];
    const result = shape.safeParse(props.value ?? "");
    return result.success ? undefined : result.error.issues[0]?.message;
  };
